import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface BankAccountVerificationProps {
  domain: string;
}

interface BankAccount {
  id: string;
  teacherId: string;
  teacherName: string;
  employeeId: string;
  accountNumber: string;
  bankName: string;
  branchName: string;
  ifscCode: string;
  tenantId: string;
  verified: boolean;
  verifiedBy: string;
  verifiedDate: string;
  lastUpdatedBy: string;
  lastUpdatedDate: string;
  timestamp: number;
  remarks: string;
}

const BankAccountVerification: React.FC<BankAccountVerificationProps> = ({ domain }) => {
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState<'all' | 'verified' | 'unverified'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAccount, setSelectedAccount] = useState<BankAccount | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState<Partial<BankAccount>>({});

  useEffect(() => {
    loadBankAccounts();
  }, [domain]);

  const loadBankAccounts = async () => {
    setLoading(true);
    try {
      const accountsRef = collection(db, `school-data/${domain}/bank-accounts`);
      const snapshot = await getDocs(accountsRef);

      const accountsList: BankAccount[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as BankAccount));

      // Sort by timestamp (newest first)
      accountsList.sort((a, b) => b.timestamp - a.timestamp);

      setBankAccounts(accountsList);
    } catch (error: any) {
      console.error('Error loading bank accounts:', error);
      alert(`Failed to load bank accounts: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (account: BankAccount) => {
    if (!confirm(`Verify bank account for ${account.teacherName}?`)) {
      return;
    }

    setProcessing(true);
    try {
      const userDataString = localStorage.getItem('userData');
      const currentUser = userDataString ? JSON.parse(userDataString) : null;
      const currentUserEmail = currentUser?.email || 'admin';

      const accountRef = doc(db, `school-data/${domain}/bank-accounts`, account.id);
      await updateDoc(accountRef, {
        verified: true,
        verifiedBy: currentUserEmail,
        verifiedDate: new Date().toISOString().split('T')[0],
        timestamp: Date.now(),
      });

      alert('Bank account verified successfully!');
      loadBankAccounts();
    } catch (error: any) {
      console.error('Error verifying account:', error);
      alert(`Failed to verify account: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleUnverify = async (account: BankAccount) => {
    if (!confirm(`Mark account as unverified for ${account.teacherName}?`)) {
      return;
    }

    setProcessing(true);
    try {
      const accountRef = doc(db, `school-data/${domain}/bank-accounts`, account.id);
      await updateDoc(accountRef, {
        verified: false,
        verifiedBy: '',
        verifiedDate: '',
        timestamp: Date.now(),
      });

      alert('Account marked as unverified!');
      loadBankAccounts();
    } catch (error: any) {
      console.error('Error unverifying account:', error);
      alert(`Failed to unverify account: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleEdit = (account: BankAccount) => {
    setSelectedAccount(account);
    setEditForm({
      accountNumber: account.accountNumber,
      bankName: account.bankName,
      branchName: account.branchName,
      ifscCode: account.ifscCode,
      remarks: account.remarks,
    });
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedAccount) return;

    if (!editForm.accountNumber || !editForm.bankName || !editForm.ifscCode) {
      alert('Please fill in all required fields (Account Number, Bank Name, IFSC Code)');
      return;
    }

    if (!confirm('Save changes to bank account details?')) {
      return;
    }

    setProcessing(true);
    try {
      const userDataString = localStorage.getItem('userData');
      const currentUser = userDataString ? JSON.parse(userDataString) : null;
      const currentUserEmail = currentUser?.email || 'admin';

      const accountRef = doc(db, `school-data/${domain}/bank-accounts`, selectedAccount.id);
      await updateDoc(accountRef, {
        accountNumber: editForm.accountNumber,
        bankName: editForm.bankName,
        branchName: editForm.branchName || '',
        ifscCode: editForm.ifscCode,
        remarks: editForm.remarks || '',
        lastUpdatedBy: currentUserEmail,
        lastUpdatedDate: new Date().toISOString().split('T')[0],
        timestamp: Date.now(),
        // Mark as unverified after edit
        verified: false,
        verifiedBy: '',
        verifiedDate: '',
      });

      alert('Bank account updated successfully! Account marked as unverified.');
      setEditMode(false);
      setSelectedAccount(null);
      loadBankAccounts();
    } catch (error: any) {
      console.error('Error updating account:', error);
      alert(`Failed to update account: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setSelectedAccount(null);
    setEditForm({});
  };

  const filteredAccounts = bankAccounts
    .filter(account => {
      if (filter === 'verified') return account.verified;
      if (filter === 'unverified') return !account.verified;
      return true;
    })
    .filter(account => {
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      return (
        account.teacherName.toLowerCase().includes(searchLower) ||
        account.employeeId.toLowerCase().includes(searchLower) ||
        account.accountNumber.toLowerCase().includes(searchLower) ||
        account.bankName.toLowerCase().includes(searchLower) ||
        account.ifscCode.toLowerCase().includes(searchLower)
      );
    });

  const verifiedCount = bankAccounts.filter(a => a.verified).length;
  const unverifiedCount = bankAccounts.filter(a => !a.verified).length;

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-bank me-2 text-primary-custom"></i>
          Bank Account Verification
        </h5>
        <small className="text-muted">Verify and manage teacher bank account details</small>
      </div>

      <div className="card-body">
        {/* Statistics */}
        <div className="row mb-4">
          <div className="col-md-4">
            <div className="card bg-light">
              <div className="card-body text-center">
                <h3 className="mb-0">{bankAccounts.length}</h3>
                <small className="text-muted">Total Accounts</small>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card bg-success bg-opacity-10">
              <div className="card-body text-center">
                <h3 className="mb-0 text-success">{verifiedCount}</h3>
                <small className="text-muted">Verified</small>
              </div>
            </div>
          </div>
          <div className="col-md-4">
            <div className="card bg-warning bg-opacity-10">
              <div className="card-body text-center">
                <h3 className="mb-0 text-warning">{unverifiedCount}</h3>
                <small className="text-muted">Unverified</small>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-3">
          <div className="col-md-6">
            <div className="btn-group w-100" role="group">
              <button
                type="button"
                className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setFilter('all')}
              >
                All ({bankAccounts.length})
              </button>
              <button
                type="button"
                className={`btn ${filter === 'verified' ? 'btn-success' : 'btn-outline-success'}`}
                onClick={() => setFilter('verified')}
              >
                Verified ({verifiedCount})
              </button>
              <button
                type="button"
                className={`btn ${filter === 'unverified' ? 'btn-warning' : 'btn-outline-warning'}`}
                onClick={() => setFilter('unverified')}
              >
                Unverified ({unverifiedCount})
              </button>
            </div>
          </div>
          <div className="col-md-6">
            <input
              type="text"
              className="form-control"
              placeholder="Search by name, employee ID, account number, bank..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading bank accounts...</p>
          </div>
        ) : filteredAccounts.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-inbox display-1 text-muted"></i>
            <p className="mt-3 text-muted">
              {searchTerm
                ? 'No bank accounts found matching your search.'
                : 'No bank accounts found.'}
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-hover">
              <thead className="table-light">
                <tr>
                  <th>Teacher Details</th>
                  <th>Account Number</th>
                  <th>Bank Details</th>
                  <th>IFSC Code</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((account) => (
                  <tr key={account.id}>
                    <td>
                      <strong>{account.teacherName}</strong>
                      <br />
                      <small className="text-muted">
                        {account.employeeId || account.teacherId}
                      </small>
                    </td>
                    <td>
                      <code>{account.accountNumber}</code>
                    </td>
                    <td>
                      <strong>{account.bankName}</strong>
                      {account.branchName && (
                        <>
                          <br />
                          <small className="text-muted">{account.branchName}</small>
                        </>
                      )}
                    </td>
                    <td>
                      <code>{account.ifscCode}</code>
                    </td>
                    <td>
                      {account.verified ? (
                        <div>
                          <span className="badge bg-success mb-1">
                            <i className="bi bi-check-circle me-1"></i>
                            Verified
                          </span>
                          {account.verifiedBy && (
                            <small className="d-block text-muted">
                              By: {account.verifiedBy}
                              <br />
                              On: {account.verifiedDate}
                            </small>
                          )}
                        </div>
                      ) : (
                        <span className="badge bg-warning">
                          <i className="bi bi-exclamation-triangle me-1"></i>
                          Unverified
                        </span>
                      )}
                    </td>
                    <td>
                      {account.lastUpdatedDate && (
                        <>
                          <small className="text-muted">
                            {account.lastUpdatedDate}
                            <br />
                            {account.lastUpdatedBy}
                          </small>
                        </>
                      )}
                      {account.remarks && (
                        <div className="mt-1">
                          <small className="text-info">
                            <i className="bi bi-info-circle me-1"></i>
                            {account.remarks}
                          </small>
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="btn-group btn-group-sm">
                        {!account.verified ? (
                          <button
                            className="btn btn-success"
                            onClick={() => handleVerify(account)}
                            disabled={processing}
                            title="Verify Account"
                          >
                            <i className="bi bi-check-circle"></i>
                          </button>
                        ) : (
                          <button
                            className="btn btn-warning"
                            onClick={() => handleUnverify(account)}
                            disabled={processing}
                            title="Mark as Unverified"
                          >
                            <i className="bi bi-x-circle"></i>
                          </button>
                        )}
                        <button
                          className="btn btn-primary"
                          onClick={() => handleEdit(account)}
                          disabled={processing}
                          title="Edit Details"
                        >
                          <i className="bi bi-pencil"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      </div>

      {/* Edit Modal */}
      {editMode && selectedAccount && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={handleCancelEdit}
        >
          <div
            className="modal-dialog modal-lg modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-pencil-square me-2"></i>
                  Edit Bank Account - {selectedAccount.teacherName}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCancelEdit}
                ></button>
              </div>
              <div className="modal-body">
                <div className="alert alert-warning">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <strong>Note:</strong> Editing bank account details will automatically mark the
                  account as unverified and require re-verification.
                </div>

                <div className="row g-3">
                  <div className="col-md-12">
                    <label className="form-label">
                      Account Number <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.accountNumber || ''}
                      onChange={(e) =>
                        setEditForm({ ...editForm, accountNumber: e.target.value })
                      }
                      placeholder="Enter account number"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      Bank Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.bankName || ''}
                      onChange={(e) => setEditForm({ ...editForm, bankName: e.target.value })}
                      placeholder="e.g., HDFC Bank"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Branch Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.branchName || ''}
                      onChange={(e) => setEditForm({ ...editForm, branchName: e.target.value })}
                      placeholder="e.g., Mumbai"
                    />
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">
                      IFSC Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.ifscCode || ''}
                      onChange={(e) =>
                        setEditForm({ ...editForm, ifscCode: e.target.value.toUpperCase() })
                      }
                      placeholder="e.g., HDFC0000501"
                      maxLength={11}
                    />
                    <small className="text-muted">11 characters</small>
                  </div>

                  <div className="col-md-6">
                    <label className="form-label">Remarks</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.remarks || ''}
                      onChange={(e) => setEditForm({ ...editForm, remarks: e.target.value })}
                      placeholder="Optional notes"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelEdit}
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveEdit}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-save me-2"></i>
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BankAccountVerification;
